import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CalendarPlus, Check, Copy, Download, Loader2, Search } from 'lucide-react';
import { buildVoucherBookingWidgetLink } from '@shared/legal/widgetTerms/hostingUrl';
import { getCurrencySymbol } from '@shared/utils/currencyOptions';
import { getApiBaseUrl } from '../api/config';
import { resolveShopTheme, themeCssVars, type ShopBranding, type ShopTheme } from '../theme';
import { useI18n } from '../i18n';
import { formatLongDate, formatMoney } from '../i18n/format';
import type { ShopLegal } from '../legal';
import VoucherCard from '../components/VoucherCard';
import WalletButtons from '../components/WalletButtons';
import { AccentButton, InkButton, QuietButton, ShopFooter, SmallCaps } from '../components/ShopChrome';
import { downloadVoucherPdf, voucherPdfStrings } from '../utils/voucherPdf';

interface PublicVoucherData {
  code: string;
  amount: number;
  remaining_amount: number;
  currency: string;
  status: 'active' | 'redeemed' | 'expired' | 'cancelled';
  expiry_date: string;
  recipient_name?: string;
  message?: string;
  location_name: string;
  location_address?: string;
  location_id?: string;
  is_expired: boolean;
  is_redeemable: boolean;
}

type StatusTone = 'positive' | 'neutral' | 'spent';

/** The message key and the tone that goes with a voucher's state. */
function statusOf(voucher: PublicVoucherData): { key: string; tone: StatusTone } {
  if (voucher.is_expired || voucher.status === 'expired') return { key: 'voucher.status_expired', tone: 'spent' };
  if (voucher.status === 'cancelled') return { key: 'voucher.status_cancelled', tone: 'spent' };
  if (voucher.status === 'redeemed' || voucher.remaining_amount <= 0) {
    return { key: 'voucher.status_spent', tone: 'neutral' };
  }
  return { key: 'voucher.status_active', tone: 'positive' };
}

const ViewVoucherPage: React.FC = () => {
  const { code: codeFromUrl } = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const { t, locale, applyVenueLocale } = useI18n();
  const [voucherCode, setVoucherCode] = useState<string>(codeFromUrl || '');
  const [loading, setLoading] = useState<boolean>(!!codeFromUrl);
  const [error, setError] = useState<string | null>(null);
  const [voucherData, setVoucherData] = useState<PublicVoucherData | null>(null);
  const [branding, setBranding] = useState<ShopBranding | null>(null);
  const [website, setWebsite] = useState<string | null>(null);
  /** The venue lets guests link a voucher while booking online (widget setting). */
  const [voucherBookingEnabled, setVoucherBookingEnabled] = useState(false);
  const [legal, setLegal] = useState<ShopLegal | null>(null);
  const [copied, setCopied] = useState(false);

  const theme = useMemo(() => resolveShopTheme(branding), [branding]);

  useEffect(() => {
    if (!codeFromUrl) return;
    let active = true;

    const fetchVoucher = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiBaseUrl = getApiBaseUrl();
        const response = await fetch(
          `${apiBaseUrl}/gift-vouchers/public/${encodeURIComponent(codeFromUrl.trim().toUpperCase())}`
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          if (!active) return;
          setError(errorData.error?.message || t('voucher.not_found'));
          setVoucherData(null);
          setLoading(false);
          return;
        }

        const data: PublicVoucherData = await response.json();
        if (!active) return;
        setVoucherData(data);

        if (data.location_id) {
          try {
            const shopResponse = await fetch(`${apiBaseUrl}/gift-vouchers/shop/${data.location_id}`);
            if (shopResponse.ok && active) {
              const shopData = await shopResponse.json();
              setBranding(shopData.branding || null);
              setWebsite(shopData.location_website || null);
              setVoucherBookingEnabled(shopData.widget_voucher_linking === true);
              setLegal(shopData.legal || null);
              applyVenueLocale(shopData.location_language);
            }
          } catch {
            /* the voucher reads fine on the design defaults */
          }
        }
      } catch (err) {
        console.error('Error fetching voucher:', err);
        if (active) {
          setError(t('voucher.load_failed'));
          setVoucherData(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    void fetchVoucher();
    return () => {
      active = false;
    };
  }, [codeFromUrl, t, applyVenueLocale]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (voucherCode.trim().length > 0) {
      navigate(`/voucher/${encodeURIComponent(voucherCode.trim().toUpperCase())}`);
    }
  };

  const handleCopyCode = () => {
    if (!voucherData?.code) return;
    void navigator.clipboard.writeText(voucherData.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPdf = async () => {
    if (!voucherData) return;
    await downloadVoucherPdf(
      {
        code: voucherData.code,
        amount: voucherData.amount,
        currency: voucherData.currency,
        currencySymbol: getCurrencySymbol(voucherData.currency),
        venueName: voucherData.location_name,
        venueAddress: voucherData.location_address,
        expiryDate: voucherData.expiry_date,
        message: voucherData.message,
        recipientName: voucherData.recipient_name,
      },
      theme,
      locale,
      voucherPdfStrings(t)
    );
  };

  const currencySymbol = voucherData ? getCurrencySymbol(voucherData.currency) : '';
  const status = voucherData ? statusOf(voucherData) : null;
  const spent = voucherData ? voucherData.amount - voucherData.remaining_amount : 0;
  const bookingUrl =
    voucherData && voucherBookingEnabled && voucherData.is_redeemable && voucherData.location_id
      ? buildVoucherBookingWidgetLink({
          locationId: voucherData.location_id,
          voucherCode: voucherData.code,
          restaurantName: voucherData.location_name,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        })
      : null;

  return (
    <div className="flex min-h-screen flex-col" style={themeCssVars(theme)}>
      <main className="mx-auto w-full max-w-[640px] flex-1 px-5 py-12 sm:px-10 sm:py-16">
        {/* Code entry — the way in for anyone holding a printed voucher */}
        {!voucherData && !loading && (
          <>
            <SmallCaps theme={theme}>{t('voucher.check_label')}</SmallCaps>
            <h1
              style={{
                margin: '10px 0 8px',
                font: `500 clamp(26px, 5vw, 34px)/1.15 ${theme.fontFamily}`,
                letterSpacing: '-.02em',
                color: theme.ink,
              }}
            >
              {t('voucher.enter_title')}
            </h1>
            <p style={{ margin: '0 0 28px', font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted }}>
              {t('voucher.enter_body')}
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row">
              <input
                type="text"
                value={voucherCode}
                onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                placeholder="A1B2C3D4"
                autoFocus
                required
                className="flex-1"
                style={{
                  padding: '15px 16px',
                  background: theme.surface,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: theme.radius,
                  font: `500 19px/1 ${theme.monoFamily}`,
                  letterSpacing: '.16em',
                  color: theme.ink,
                  outline: 'none',
                }}
              />
              <AccentButton theme={theme} type="submit">
                <Search className="h-4 w-4" />
                {t('voucher.view')}
              </AccentButton>
            </form>

            {error && (
              <p className="mt-4" style={{ font: `400 14px/1.5 ${theme.fontFamily}`, color: theme.accent }}>
                {error}
              </p>
            )}
          </>
        )}

        {loading && (
          <div className="flex flex-col items-center gap-4 py-20">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: theme.accent }} />
            <p style={{ font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted, margin: 0 }}>
              {t('voucher.looking_up')}
            </p>
          </div>
        )}

        {error && codeFromUrl && !loading && !voucherData && (
          <div className="py-10">
            <h1 style={{ font: `500 26px/1.2 ${theme.fontFamily}`, color: theme.ink, margin: '0 0 8px' }}>
              {error}
            </h1>
            <p style={{ font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted, margin: '0 0 24px' }}>
              {t('voucher.not_found_body')}
            </p>
            <AccentButton
              theme={theme}
              onClick={() => {
                setVoucherCode('');
                setError(null);
                navigate('/voucher');
              }}
            >
              {t('voucher.try_another')}
            </AccentButton>
          </div>
        )}

        {voucherData && !loading && (
          <>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <div>
                <SmallCaps theme={theme}>{t('voucher.your_voucher')}</SmallCaps>
                <div
                  className="mt-2"
                  style={{ font: `500 22px/1.2 ${theme.fontFamily}`, color: theme.ink }}
                >
                  {voucherData.location_name}
                </div>
              </div>
              {status && <StatusChip theme={theme} label={t(status.key)} tone={status.tone} />}
            </div>

            <VoucherCard
              theme={theme}
              amount={voucherData.amount}
              currencySymbol={currencySymbol}
              currency={voucherData.currency}
              venueName={voucherData.location_name}
              venueAddress={voucherData.location_address}
              code={voucherData.code}
              expiryDate={voucherData.expiry_date}
              message={voucherData.message}
              remainingAmount={voucherData.remaining_amount}
            />

            <div
              className="mt-6 flex flex-col gap-3 p-5"
              style={{ background: theme.surface, border: `1px solid ${theme.hairline}`, borderRadius: theme.radius }}
            >
              <BalanceRow
                theme={theme}
                label={t('voucher.original_value')}
                value={formatMoney(voucherData.amount, voucherData.currency, locale)}
              />
              <BalanceRow
                theme={theme}
                label={t('voucher.remaining')}
                value={formatMoney(voucherData.remaining_amount, voucherData.currency, locale)}
                strong
              />
              {spent > 0 && (
                <>
                  <div className="h-px" style={{ background: theme.hairlineSoft }} />
                  <BalanceRow
                    theme={theme}
                    label={t('voucher.already_spent')}
                    value={formatMoney(spent, voucherData.currency, locale)}
                    quiet
                  />
                </>
              )}
            </div>

            {bookingUrl && (
              <div className="mt-4">
                <InkButton
                  theme={theme}
                  onClick={() => window.open(bookingUrl, '_blank', 'noopener,noreferrer')}
                  className="w-full"
                >
                  <CalendarPlus className="h-4 w-4" />
                  {t('voucher.book_with_voucher')}
                </InkButton>
                <p
                  className="mt-2 text-center"
                  style={{ font: `400 13px/1.5 ${theme.fontFamily}`, color: theme.muted, margin: '8px 0 0' }}
                >
                  {t('voucher.book_with_voucher_hint')}
                </p>
              </div>
            )}

            <div className="mt-4 flex flex-col gap-2.5 sm:flex-row">
              {website && !bookingUrl && (
                <InkButton
                  theme={theme}
                  onClick={() => window.open(website, '_blank', 'noopener,noreferrer')}
                  className="flex-1"
                >
                  {t('voucher.book_table')}
                </InkButton>
              )}
              <QuietButton theme={theme} onClick={handleCopyCode} className="flex-1">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? t('voucher.copied') : t('voucher.copy_code')}
              </QuietButton>
              <QuietButton theme={theme} onClick={handleDownloadPdf} className="flex-1">
                <Download className="h-4 w-4" />
                {t('common.download_pdf')}
              </QuietButton>
            </div>

            <div className="mt-3">
              <WalletButtons voucherCode={voucherData.code} theme={theme} />
            </div>

            <p
              className="mt-8 pt-6"
              style={{
                borderTop: `1px solid ${theme.hairline}`,
                font: `400 14px/1.6 ${theme.fontFamily}`,
                color: theme.muted,
                margin: 0,
              }}
            >
              {t('voucher.footer_note', { date: formatLongDate(voucherData.expiry_date, locale) })}
            </p>
          </>
        )}
      </main>

      <ShopFooter
        theme={theme}
        venueName={voucherData?.location_name}
        venueAddress={voucherData?.location_address}
        legal={legal || undefined}
        locationId={voucherData?.location_id}
      />
    </div>
  );
};

const StatusChip: React.FC<{ theme: ShopTheme; label: string; tone: StatusTone }> = ({ theme, label, tone }) => {
  const palette =
    tone === 'positive'
      ? { bg: theme.positiveSurface, border: theme.positiveBorder, color: theme.positive }
      : tone === 'spent'
        ? { bg: theme.surface, border: theme.inputBorder, color: theme.accent }
        : { bg: theme.surface, border: theme.inputBorder, color: theme.label };

  return (
    <span
      className="px-3 py-1.5"
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: Math.min(theme.radius, 2),
        font: `600 11px/1 ${theme.fontFamily}`,
        letterSpacing: '.14em',
        textTransform: 'uppercase',
        color: palette.color,
      }}
    >
      {label}
    </span>
  );
};

const BalanceRow: React.FC<{
  theme: ShopTheme;
  label: string;
  value: string;
  strong?: boolean;
  quiet?: boolean;
}> = ({ theme, label, value, strong = false, quiet = false }) => (
  <div
    className="flex justify-between gap-4"
    style={{ font: `400 ${quiet ? '13px' : '14px'}/1.4 ${theme.fontFamily}`, color: quiet ? theme.label : theme.body }}
  >
    <span>{label}</span>
    <span
      style={{
        color: quiet ? theme.label : theme.ink,
        fontWeight: strong ? 500 : 400,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </span>
  </div>
);

export default ViewVoucherPage;
